import CheckCircleIcon from 'components/icons/CheckCircleIcon';
import StethescopeIcon from 'components/icons/StethescopeIcon';
import HospitalIcon from 'components/icons/HospitalIcon';
import Tooltip from 'components/Tooltip';
import PlusSignIcon from 'components/icons/PlusSignIcon';
import PillsIcon from 'components/icons/PillsIcon';
import WarningDiamondIcon from 'components/icons/WarningDiamondIcon';
import Image from 'next/image';
const PartsOfMedicare = () => {
  return (
    <section className="lfe-page-container parts-of-medicare">
      <h1 className="title">What are the parts of Medicare?</h1>

      <div className="original-medicare-container">
        {/* Title */}
        <div className="border-large">
          <h2>
            Original <b>Medicare</b>{' '}
            <Tooltip
              verticalAlign="bottom"
              color="white"
              subject="Original Medicare"
            >
              Parts A and B were the only parts of Medicare when it was
              originally created in 1965.
            </Tooltip>
          </h2>
          <p>(provided by the government)</p>
        </div>

        {/* Original Medicare */}
        <div className="original-medicare-card">
          {/* Parts A and B cards */}
          <div className="parts-a-and-b-container">
            {/* Part A card */}
            <div className="part-a-card">
              <div className="part-a-title">
                <h3>
                  <b>Part A:</b> Hospital Insurance
                </h3>
                <div className="circle-badge">
                  <HospitalIcon />
                </div>
              </div>
              <div className="coverage-list">
                <h4>Helps cover:</h4>
                <ul>
                  <li>
                    <CheckCircleIcon color="lightpurple" />
                    <span>In-patient hospital care</span>
                  </li>
                  <li>
                    <CheckCircleIcon color="lightpurple" />
                    <span>Skilled nursing facilities</span>
                  </li>
                  <li>
                    <CheckCircleIcon color="lightpurple" />
                    <span>Hospice care</span>
                  </li>
                  <li>
                    <CheckCircleIcon color="lightpurple" />
                    <span>Home health care</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Part B card */}
            <div className="part-b-card">
              <div className="part-b-title">
                <h3>
                  <b>Part B:</b> Medical Insurance
                </h3>
                <div className="circle-badge">
                  <StethescopeIcon />
                </div>
              </div>
              <div className="coverage-list">
                <h4>Helps cover:</h4>
                <ul>
                  <li>
                    <CheckCircleIcon color="blue" />
                    <span>Provider services</span>
                  </li>
                  <li>
                    <CheckCircleIcon color="blue" />
                    <span>Outpatient care</span>
                  </li>
                  <li>
                    <CheckCircleIcon color="blue" />
                    <span>Home health care</span>
                  </li>
                  <li>
                    <CheckCircleIcon color="blue" />
                    <span>Medical equipment</span>
                    <Tooltip
                      horizontalAlign="left"
                      color="blue"
                      subject="Medical equipment"
                    >
                      Medical equipment that will last at least three years and
                      that can be used repeatedly, often in the home, for a
                      medical reason. Like wheelchairs, walkers, and hospital
                      beds. This kind of equipment is officially known as
                      &ldquo;durable medical equipment.&rdquo;
                    </Tooltip>
                  </li>
                  <li>
                    <CheckCircleIcon color="blue" />
                    <span>Preventive services</span>
                    <Tooltip
                      horizontalAlign="left"
                      color="blue"
                      subject="Preventive services"
                    >
                      Services such as screenings, vaccinations, counseling,
                      wellness visits, and more.
                    </Tooltip>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Railroad note */}
          <div className="note">
            <WarningDiamondIcon />
            <span>
              If you are enrolled in Social Security or Railroad Retirement
              Benefits, you are automatically enrolled in Parts A and B at age
              65.
              <Tooltip
                horizontalAlign="right"
                color="blue"
                subject="Parts A and B enrollment"
              >
                You can opt out and delay enrollment in Parts A and B. However,
                you could have penalty costs if you do not have medical coverage
                at least as good as Medicare while your Medicare enrollment is
                delayed.
              </Tooltip>
            </span>
          </div>
        </div>
      </div>

      {/* Plus sign */}
      <div className="plus-sign">
        <PlusSignIcon size="60" color="purple" />
      </div>

      {/* Part D */}
      <div className="part-d-card">
        <div className="part-d-title">
          <div>
            <h2>
              <b>Part D:</b> Drug Coverage
            </h2>
            <p>(provided by private insurance companies)</p>
          </div>
          <div className="circle-badge">
            <PillsIcon />
          </div>
        </div>

        <div className="part-d-cols">
          <div className="coverage-list">
            <h4>Helps cover:</h4>
            <ul>
              <li>
                <CheckCircleIcon color="peach" />
                <span>Prescription drugs</span>
              </li>
              <li>
                <CheckCircleIcon color="peach" />
                <span>Vaccines not covered by Part B</span>
              </li>
            </ul>
          </div>
          <div className="note">
            <WarningDiamondIcon />
            <span>
              Unlike Original Medicare, you are NOT automatically enrolled in
              Part D if you are enrolled in Social Security or Railroad
              Retirement Benefits. You need to enroll in a plan with drug
              coverage yourself.
              <Tooltip color="blue" subject="Part D enrollment">
                If you do not enroll in a Part D or Medicare Advantage plan with
                prescription coverage when you are eligible, you could have
                penalty costs.
              </Tooltip>
            </span>
          </div>
        </div>
      </div>

      {/* Additional options */}
      <div className="additional-options">
        <p>
          But Original Medicare has more gaps than just missing prescription
          coverage. Here are{' '}
          <strong>two possible options for expanding your coverage</strong>{' '}
          beyond Original Medicare(Parts A and B), to help fill those gaps.
        </p>
      </div>

      <div className="options-container">
        <div className="option-card med-supp-plan">
          <div className="tab">Option 1</div>
          <div className="tab-content">
            <div className="tab-content-title">
              <h2>
                <b>Medicare Supplement</b> (Medigap)
              </h2>
              <p>(provided by private insurance companies)</p>
            </div>
            <div className="coverage-list">
              <h4>Helps cover:</h4>
              <ul>
                <li>
                  <CheckCircleIcon color="blue" />
                  <span>
                    Some gaps in coverage left by Parts A and B. It&rsquo;s sold
                    as a separate plan that adds on to Original Medicare.
                  </span>
                </li>
              </ul>
            </div>

            <div className="plus-sign">
              <PlusSignIcon size="25" color="blue" />
            </div>

            <div className="nested-card">
              <div className="nested-card-title">
                <h4>
                  <b>Part D:</b> Drug Coverage
                </h4>
              </div>
              <div className="note">
                <WarningDiamondIcon />
                <span>
                  <strong>Note:</strong>
                  <br></br>
                  Medicare Supplement does not include coverage for
                  prescriptions. If enrolling in a Medicare Supplement plan, you
                  must enroll in Part D separately to get drug coverage.
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="option-card ma-plan">
          <div className="tab">Option 2</div>
          <div className="tab-content">
            <div className="tab-content-title">
              <h2>
                <b>Part C:</b> Medicare Advantage (MA)
              </h2>
              <p>(provided by private insurance companies)</p>
            </div>
            <div className="coverage-list">
              <h4>Helps cover:</h4>
              <ul>
                <li>
                  <CheckCircleIcon color="darkpurple" />
                  <span>
                    Costs normally covered by Parts A, B, (and usually D) by
                    bundling them into one plan.
                  </span>
                </li>
              </ul>
            </div>

            <div className="triangle-badge-container">
              <Image
                src="/triangle-badges-3x.png"
                alt="hospital insurance, medical insurance, and drug coverage icon"
                width={195}
                height={73}
              />
            </div>

            <div className="note">
              <WarningDiamondIcon />
              <span>
                <strong>Note:</strong>
                <br />
                <ol>
                  <li>
                    Prescription coverage is usually included in Medicare
                    Advantage, but on rare occasions requires enrolling in Part
                    D separately.
                  </li>
                  <li>
                    Even on a Medicare Advantage plan, you still have to pay
                    premiums for Part A (if you have a Part A premium) and for
                    Part B.
                  </li>
                </ol>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="circle-gradient-background">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1280 792"
          preserveAspectRatio="none"
        >
          <path
            d="M-7 792L-7 123.255C163.758 46.6852 390.802 0 640 0C888.226 0 1114.47 46.3218 1285 122.361V792H-7Z"
            fill="url(#paint0_linear_369_1250)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_369_1250"
              x1="-7"
              y1="396"
              x2="1479.2"
              y2="396"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="var(--purple-200)" />
              <stop offset="1" stopColor="var(--blue-300)" />
            </linearGradient>
          </defs>
        </svg>
        <div className="pros-and-cons-container">
          <div className="pros-and-cons-title">
            <h2>
              <b>Medicare Supplement</b> and <b>Medicare Advantage</b> each have
              their upsides and downsides.
            </h2>
          </div>

          <div className="list-card-container">
            <div className="list-card">
              <div className="list-card-title">
                <h3>Medicare Supplement Plans</h3>
              </div>
              <ul className="list-card-content">
                <li>
                  Offer flexibility to see any provider that accepts Medicare.
                </li>
                <li>
                  Generally have higher premiums than Medicare Advantage Plans,
                  but you may pay less for services as you use them.
                </li>
                <li>
                  If you don&rsquo;t use the coverage, you&rsquo;ve still spent
                  the money on premiums.
                </li>
                <li>
                  You need a separate Part D drug plan, with its own premium.
                </li>
                <li>
                  Rarely include extra benefits beyond coverage for Parts A and
                  B.
                </li>
              </ul>
            </div>
            <div className="list-card">
              <div className="list-card-title ma-plan">
                <h3>Medicare Advantage Plans</h3>
              </div>
              <ul className="list-card-content">
                <li>
                  Have a network of providers, and only sometimes provide
                  out-of-network coverage.
                </li>
                <li>
                  Up-front costs are usually lower than with Medicare
                  Supplement, but you may pay more overall as you use care
                  services.
                </li>
                <li>
                  Have an out-of-pocket max, but it doesn&rsquo;t always apply
                  to all types of health costs.
                </li>
                <li>
                  May require you get prior authorization for services and
                  supplies.
                </li>
                <li>May include extra benefits.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartsOfMedicare;
